import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadialBarChart, RadialBar, PolarAngleAxis
} from "recharts";

import { baseUrl } from "./Univ";

function Home() {

  const today = new Date().toISOString().substring(0,10);

  // =========================
  // STATES
  // =========================
  const COLORS = [
    "#00C49F",
    "#0088FE",
    "#FFBB28",
    "#FF8042",
    "#FF4444"
  ];

// =====================================
// PHARMACY STATES
// =====================================
const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [combinedData, setcombinedData] = useState([]);
  const [viewPharmData, setViewPharmData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ---------------- FETCH SALES ----------------
  async function fetchData() {
    setIsLoading(true);

    const start = startDate ? startDate.toISOString().substring(0, 10) : "";
    const end = endDate ? endDate.toISOString().substring(0, 10) : "";

    try {
      const res = await axios.get(
        `${baseUrl}all-pharm-sale/?start_date=${start}&end_date=${end}&status=1`
      );

      setcombinedData(res.data);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  // ---------------- FETCH PHARMACY DATA ----------------
  async function fetchPharmData() {
    try {
      const productIds = combinedData.reduce((ids, row) => {
        const products = (row.product || "")
          .toString()
          .split(",")
          .map(i => i.trim())
          .filter(Boolean);

        return [...ids, ...products];
      }, []);

      const uniqueIds = [...new Set(productIds)];

      const indentResponses = await Promise.all(
        uniqueIds.map(id =>
          axios.get(`${baseUrl}update-indent-use/${id}/`)
        )
      );

      const saleToPharmaMap = {};

      indentResponses.forEach(res => {
        saleToPharmaMap[res.data.id] = res.data.product;
      });

      const pharmaIds = [...new Set(Object.values(saleToPharmaMap))];

      const pharmResponses = await Promise.all(
        pharmaIds.map(id =>
          axios.get(`${baseUrl}update-pharm-qty/${id}/`)
        )
      );

      const pharmaMap = {};
      pharmResponses.forEach(res => {
        pharmaMap[res.data.id] = res.data;
      });

      const updated = combinedData.map(row => {
        const ids = (row.product || "").split(",");
        const qtys = (row.qty || "").split(",");

        return {
          ...row,
          enriched: ids.map((saleId, i) => {
            const pharmaId = saleToPharmaMap[saleId];
            return {
              qty: parseFloat(qtys[i] || 0),
              data: pharmaMap[pharmaId] || null
            };
          })
        };
      });

      setRows(updated);
      setViewPharmData(pharmaMap);
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (combinedData.length > 0) {
      fetchPharmData();
    }
  }, [combinedData]);

  // ---------------- CALCULATIONS ----------------
  const totalMrp = rows.reduce((t, r) => {
    let s = 0;
    (r.enriched || []).forEach(i => {
      if (i.data) s += parseFloat(i.data.mrp || 0) * i.qty;
    });
    return t + s;
  }, 0);

  const totalPurchase = rows.reduce((t, r) => {
    let s = 0;
    (r.enriched || []).forEach(i => {
      if (i.data) s += parseFloat(i.data.pprice || 0) * i.qty;
    });
    return t + s;
  }, 0);

  const totalPaid = rows.reduce(
    (t, r) => t + parseFloat(r.paid || 0),
    0
  );

  const profit = totalMrp - totalPurchase;
  const profitPercent =
    totalPurchase > 0 ? ((profit / totalPurchase) * 100).toFixed(2) : 0;

// =====================================
// DISCOUNT
// =====================================

const totalDiscount = rows.reduce((total,row) => {

  return total + Number(
    row.discount || 0
  );

},0);


// =====================================
// NET SALES
// =====================================

const netSales =
  totalMrp - totalDiscount;


// =====================================
// GROSS PROFIT
// =====================================

const grossProfit =
  netSales - totalPurchase;


// =====================================
// PROFIT %
// =====================================

const profitPercentage =
  totalPurchase > 0
  ? (
      (grossProfit / totalPurchase) * 100
    ).toFixed(2)
  : 0;


// =====================================
// GRAPH
// =====================================

const pharmacyProfitGraph = [

  {
    name:"MRP",
    amount:totalMrp
  },

  {
    name:"Discount",
    amount:totalDiscount
  },

  {
    name:"Net Sales",
    amount:netSales
  },

  {
    name:"Purchase",
    amount:totalPurchase
  },

  {
    name:"Profit",
    amount:grossProfit
  }

];
  // =========================
  // UI
  // =========================

  return(

    <section
      className="container-fluid p-4"
      style={{
        background:"#0f172a",
        minHeight:"100vh",
        color:"white"
      }}
    >

      {/* HEADER */}

      <div className="d-flex justify-content-between align-items-center mb-4">

        <h2 style={{fontWeight:"bold"}}>
          Hospital Command Center
        </h2>

        <h5>
          {new Date().toLocaleString()}
        </h5>

      </div>

<div className="col-md-12 mt-4">

  <div className="card shadow-lg">

    <div
      className="card-header"
      style={{
        background:"#0f172a",
        color:"white",
        fontWeight:"bold"
      }}
    >
      Pharmacy Profit Statement
    </div>

    <div className="card-body">

      <div className="row mb-4">

        <div className="col-md-2">
          <h6>Total MRP</h6>
          <h4>₹ {totalMrp.toFixed(2)}</h4>
        </div>

        <div className="col-md-2">
          <h6>Discount</h6>
          <h4 style={{color:"red"}}>
            ₹ {totalDiscount.toFixed(2)}
          </h4>
        </div>

        <div className="col-md-2">
          <h6>Net Sales</h6>
          <h4 style={{color:"green"}}>
            ₹ {netSales.toFixed(2)}
          </h4>
        </div>

        <div className="col-md-2">
          <h6>Purchase</h6>
          <h4>
            ₹ {totalPurchase.toFixed(2)}
          </h4>
        </div>

        <div className="col-md-2">
          <h6>Gross Profit</h6>
          <h4
            style={{
              color:
                grossProfit > 0
                ? "green"
                : "red"
            }}
          >
            ₹ {grossProfit.toFixed(2)}
          </h4>
        </div>

        <div className="col-md-2">
          <h6>Profit %</h6>
          <h4 style={{color:"#2563eb"}}>
            {profitPercentage}%
          </h4>
        </div>

      </div>

      <ResponsiveContainer
        width="100%"
        height={400}
      >

        <BarChart
          data={pharmacyProfitGraph}
        >

          <CartesianGrid
            strokeDasharray="3 3"
          />

          <XAxis dataKey="name" />

          <YAxis />

          <Tooltip />

          <Legend />

          <Bar
            dataKey="amount"
            fill="#3b82f6"
          />

        </BarChart>

      </ResponsiveContainer>

    </div>

  </div>

</div>

    </section>

  );

}

// =========================
// KPI CARD
// =========================

function DashboardCard({title,value,color}){

  return(

    <div className="col-md-3 col-lg-2 mb-3">

      <div
        style={{
          background:"#1e293b",
          borderRadius:"16px",
          padding:"20px",
          boxShadow:"0 4px 15px rgba(0,0,0,0.4)",
          borderLeft:`6px solid ${color}`,
          minHeight:"120px"
        }}
      >

        <h6 style={{color:"#cbd5e1"}}>
          {title}
        </h6>

        <h2
          style={{
            fontWeight:"bold",
            marginTop:"15px"
          }}
        >
          {value}
        </h2>

      </div>

    </div>

  );

}

export default Home;